import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, KeyRound, X, CheckCircle2, AlertCircle } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { AuthLayout, FormField, Input, Button } from '../ui';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtpField, setShowOtpField] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSendOtp = async () => {
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${baseurl}/api/member/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || 'Failed to send OTP');
      }

      setShowOtpField(true);
      setSnackbar({
        open: true,
        message: 'OTP sent successfully to your email',
        severity: 'success',
      });
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');

    if (!otp) {
      setError('OTP is required');
      return;
    }

    if (!/^\d{6}$/.test(otp)) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${baseurl}/api/member/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          otp: otp
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || 'Invalid OTP');
      }

      // Navigate to reset password page with email
      navigate('/reset-password', { state: { email } });
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="KBC · Account recovery"
      title={<>Forgot <em className="kbc-serif">password</em>.</>}
      subtitle={
        showOtpField
          ? 'Enter the OTP sent to your email address to continue.'
          : 'Enter your registered email address to receive a password reset OTP.'
      }
      footer={
        <>
          Remember your password?{' '}
          <Link to="/" className="font-semibold text-primary hover:text-primary-hi">
            Back to Sign In
          </Link>
        </>
      }
    >
      <div className="space-y-5">
        <FormField label="Email Address" htmlFor="email" required>
          <Input
            id="email"
            type="email"
            placeholder="Enter your registered email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={showOtpField}
            leftIcon={<Mail className="h-[18px] w-[18px]" />}
          />
        </FormField>

        {showOtpField && (
          <FormField label="OTP" htmlFor="otp" required>
            <Input
              id="otp"
              type="text"
              placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              maxLength={6}
              leftIcon={<KeyRound className="h-[18px] w-[18px]" />}
            />
          </FormField>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-md border border-danger/20 bg-danger/10 px-4 py-3 text-sm font-medium text-danger">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <Button
          onClick={showOtpField ? handleVerifyOtp : handleSendOtp}
          loading={isLoading}
          fullWidth
          size="lg"
        >
          {isLoading
            ? (showOtpField ? 'Verifying…' : 'Sending OTP…')
            : (showOtpField ? 'Verify OTP' : 'Send Reset OTP')}
        </Button>
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-slideUp">
          <div
            className={`flex items-center gap-3 rounded-full px-5 py-3 text-sm font-medium text-white shadow-lift ${
              snackbar.severity === 'success' ? 'bg-success' : 'bg-danger'
            }`}
          >
            {snackbar.severity === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
            <span>{snackbar.message}</span>
            <button onClick={() => setSnackbar({ ...snackbar, open: false })} className="opacity-80 hover:opacity-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
